import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { PANTERA, THUNDERDOME } from 'HubStyleTokens/colors';
import { JUPITER_LAYER } from 'HubStyleTokens/sizes';
import UITourPulserPath from './UITourPulserPath';
import UITourPulserShadow from './UITourPulserShadow';
import UITourPulserSVG from './UITourPulserSVG';
import { usePulserContainer, usePulserSVGPaths } from '../hooks/pulser';
const UITourPulserDefaultProps = {
  borderColor: THUNDERDOME,
  borderRadius: 3,
  borderSize: 3,
  enableBorder: true,
  enableOverlay: true,
  enablePulse: true,
  pulserDistance: 6,
  pulserDuration: '2s',
  pulserPadding: 4,
  pulserZIndex: Number(JUPITER_LAYER)
};
const isDropShadowSupported = typeof SVGFEDropShadowElement !== 'undefined';
const UITourPulser = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    attachTo,
    borderColor,
    borderRadius,
    borderSize,
    enableBorder,
    enableOverlay,
    enablePulse,
    pulserDistance,
    pulserDuration,
    pulserPadding,
    pulserZIndex,
    target
  } = props;
  const pulserContainer = usePulserContainer(target);
  const [shouldShowPulser, setShouldShowPulser] = useState(false);
  const pulserPathProps = useMemo(() => {
    return {
      padding: pulserPadding,
      distance: enablePulse ? pulserDistance : undefined,
      radius: borderRadius
    };
  }, [enablePulse, borderRadius, pulserDistance, pulserPadding]);
  const paths = usePulserSVGPaths(shouldShowPulser ? attachTo || target : null, pulserPathProps); // expose showPulser to parent component
  // This is for showing pulser after tour step popover is opened

  useImperativeHandle(ref, () => ({
    showPulser: () => setShouldShowPulser(true)
  }));

  if (!pulserContainer) {
    return null;
  }

  return /*#__PURE__*/createPortal( /*#__PURE__*/_jsxs(UITourPulserSVG, {
    className: 'tour-pulser' + (shouldShowPulser ? " tour-pulser--visible" : ""),
    zIndex: pulserZIndex,
    children: [/*#__PURE__*/_jsx("defs", {
      children: isDropShadowSupported && /*#__PURE__*/_jsx(UITourPulserShadow, {
        shadowColor: borderColor
      })
    }), enableOverlay && /*#__PURE__*/_jsx(UITourPulserPath, {
      className: "tour-pulser-overlay",
      duration: pulserDuration,
      fill: PANTERA,
      onClickCapture: event => {
        // Prevent unexpected click events bubbling up to the window
        // For example: clicking the overlay should not close the target select
        event.stopPropagation();
      },
      pathStart: paths.start.overlay,
      pathEnd: paths.end.overlay
    }), enableBorder && /*#__PURE__*/_jsx(UITourPulserPath, {
      className: "tour-pulser-border",
      duration: pulserDuration,
      fill: "none",
      filter: isDropShadowSupported ? 'url(#dropshadow)' : undefined,
      pathStart: paths.start.border,
      pathEnd: paths.end.border,
      stroke: borderColor,
      strokeWidth: borderSize
    })]
  }), pulserContainer);
});
UITourPulser.defaultProps = UITourPulserDefaultProps;
UITourPulser.propTypes = {
  attachTo: PropTypes.string,
  borderColor: PropTypes.string,
  borderRadius: PropTypes.number,
  borderSize: PropTypes.number,
  enableBorder: PropTypes.bool,
  enableOverlay: PropTypes.bool,
  enablePulse: PropTypes.bool,
  pulserDistance: PropTypes.number,
  pulserDuration: PropTypes.string,
  pulserPadding: PropTypes.number,
  pulserZIndex: PropTypes.number,
  target: PropTypes.instanceOf(Element)
};
export default UITourPulser;